import LogOutIcon from "../icons/LogOutIcon";
import roadesenseImage from "../images/roadsense_logo_new.png";
import nplLogo from "../images/npl_logo.png";
import React, { useState, useEffect } from "react";
import useInterval from "@rooks/use-interval";
import axios from "axios";
import useLogout from "../hooks/useLogout";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/Button";
import { ChevronDown } from "lucide-react";
import { Button as newButton } from "../components/ui/Button";

function getWelcomeSentence() {
  let currentHour = new Date().getHours();
  if (currentHour >= 5 && currentHour < 12) {
    return "בוקר טוב";
  } else if (currentHour >= 12 && currentHour < 17) {
    return "צהריים טובים";
  } else if (currentHour >= 17 && currentHour < 21) {
    return "ערב טוב";
  } else {
    return "לילה טוב";
  }
}

function getFormattedDate() {
  const now = new Date();
  const days = ["יום ראשון", "יום שני", "יום שלישי", "יום רביעי", "יום חמישי", "יום שישי", "שבת"];
  const months = ["בינואר", "בפברואר", "במרץ", "באפריל", "במאי", "ביוני", "ביולי", "באוגוסט", "בספטמבר", "באוקטובר", "בנובמבר", "בדצמבר"];
  const dayStr = days[now.getDay()];
  const dateNum = now.getDate();
  const monthStr = months[now.getMonth()];
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return {
    dateText: `${dayStr}, ${dateNum} ${monthStr} ${year}`,
    timeText: `${hours}:${minutes}`,
  };
}

const CUSTOMER_TITLE = "הפארק הלאומי רמת-גן";

const TopBar = ({ pageTitle, AreaName, logo, setSideBarOpen, isMobile }) => {
  const logout = useLogout();
  const navigate = useNavigate();
  const [welcomeSentence, setWelcomeSentence] = useState("");
  const [formattedDate, setFormattedDate] = useState({ dateText: "", timeText: "" });
  const [weatherText, setWeatherText] = useState("");
  const [weatherIcon, setWeatherIcon] = useState([]);

  const signOut = async () => {
    await logout().then(() => {
      navigate("/login");
    });
  };

  function updateWeather() {
    // OpenWeather city id for Ramat Gan
    axios
      .get(
        "https://api.openweathermap.org/data/2.5/weather?id=293788&units=metric&lang=he&APPID=101634096fcf8d2781bb389474b45aae"
      )
      .then(async (res) => {
        let data = res.data;
        let temp = Math.round(data["main"]["temp"]);
        let icon = `https://openweathermap.org/img/wn/${data["weather"][0]["icon"]}@2x.png`;
        let iconText = data["weather"][0]["description"];

        setWeatherText(`${temp}°C`);
        setWeatherIcon([icon, iconText]);
      })
      .catch((err) => {
        console.log("error with update weather inside topBar-> ", err);
      });
  }

  useEffect(() => {
    setWelcomeSentence(getWelcomeSentence());
    setFormattedDate(getFormattedDate());
    updateWeather();
  }, []);

  useInterval(
    () => {
      setWelcomeSentence(getWelcomeSentence());
      setFormattedDate(getFormattedDate());
    },
    5000,
    true
  );

  useInterval(
    () => {
      updateWeather();
    },
    1000 * 60 * 30,
    true
  );

  const handleMobileMenu = () => {
    setSideBarOpen(true);
  };

  // Prefer bundled NPL logo; fall back to tenant config logo if provided.
  const activeLogo = nplLogo || logo;

  return (
    <header
      dir="rtl"
      className="w-full bg-white/95 backdrop-blur-md rounded-2xl border border-gray-200/90 shadow-sm px-4 py-2.5 mb-3 flex flex-col md:flex-row items-center justify-between gap-3 text-gray-800"
    >
      {/* RIGHT SIDE (RTL): Customer Logo */}
      <div className="flex items-center gap-3">
        {isMobile && (
          <newButton className="w-8 h-8 p-1" onClick={handleMobileMenu} variant="outline" size="icon">
            <ChevronDown className="h-4 w-4" />
          </newButton>
        )}
        <div className="flex items-center gap-2.5 bg-gray-50/90 px-3 py-1.5 rounded-xl border border-gray-100 shadow-2xs">
          {activeLogo && (
            <img
              className="h-9 sm:h-11 w-auto object-contain"
              src={activeLogo}
              alt={CUSTOMER_TITLE}
            />
          )}
          <div className="flex flex-col text-right pr-2 border-r border-gray-200 justify-center">
            <span className="text-sm sm:text-base font-bold text-gray-900 leading-tight">
              {CUSTOMER_TITLE}
            </span>
          </div>
        </div>
      </div>

      {/* CENTER: Hebrew Welcome, Date, Live Time & Weather */}
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-700 bg-gray-50/80 px-3.5 py-1.5 rounded-xl border border-gray-100">
        <span className="font-bold text-gray-900">{welcomeSentence}</span>
        <span className="text-gray-300">•</span>
        <span className="font-medium text-gray-700">{formattedDate?.dateText}</span>
        <span className="font-bold text-gray-800 bg-white px-2 py-0.5 rounded-md border border-gray-200/60 shadow-2xs">
          {formattedDate?.timeText}
        </span>

        {weatherText && (
          <>
            <span className="text-gray-300 mr-0.5">•</span>
            <div className="flex items-center gap-1 text-gray-700">
              {weatherIcon[0] && (
                <img
                  className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
                  src={weatherIcon[0]}
                  alt={weatherIcon[1] || "מזג אוויר"}
                  title={weatherIcon[1]}
                />
              )}
              <span className="font-bold text-gray-900">{weatherText}</span>
              {weatherIcon[1] && (
                <span className="text-[11px] text-gray-500 hidden sm:inline">({weatherIcon[1]})</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* LEFT SIDE (RTL): RoadSense Logo + Log Out */}
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="flex items-center pl-1">
          <img className="h-5 sm:h-6 w-auto object-contain" src={roadesenseImage} alt="RoadSense" />
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={signOut}
          className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 hover:text-red-600 hover:border-red-200 hover:bg-red-50/70 transition-colors"
        >
          <span>התנתקות</span>
          <LogOutIcon />
        </Button>
      </div>
    </header>
  );
};

export default TopBar;
